import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { RestApiService } from "../rest-api/rest-api.service";
import { HttpClient } from "@angular/common/http";
import { SharedService } from "../shared-service/shared.service";
import { CategoryTree, MachineBaseDto, MachineListDto, QueryParams, SearchFilter } from "src/app/types";

@Injectable({
  providedIn: 'root',
})

export class MarketplaceService {
    private private_portal_url: string = environment.apiPrivate;
    public hostName: string;
    public shortName: string | null = null;

    constructor(
    @Inject(DOCUMENT) private document: Document,
    private restApiService: RestApiService,
    private http: HttpClient,
    private sharedService: SharedService,
    ) {
        if (this.sharedService.isBrowser()) {
            environment.host
                ? (this.hostName = environment.host)
                : (this.hostName = this.document.location.hostname);
            } else {
            this.hostName = this.sharedService.getRequestHostName();
            }
    }

    public getPrivateClientTree = async (
        token: string | null,
        shortName: string | null,
        options?: RequestInit
    ): Promise<CategoryTree> => {

        if (!token) {
            throw new Error('User is not authenticated. No access token found.');
          }

        const response = await fetch(
            `${this.private_portal_url}/client/${this.hostName}/marketplaces/${shortName}/category-tree`, {
                method: 'GET',
                headers: {
                Authorization: `Bearer ${token}`,
                ...(options?.headers ?? {}),
                },
            }
        );
        const data: CategoryTree = await response.json();
        return data;
    };

    public getPrivateMachineDetails = async (
    token: string | null,
    macId: string,
    shortName: string | null,
    options?: RequestInit
    ): Promise<MachineBaseDto> => {
    const response = await fetch(
        `${this.private_portal_url}/client/${this.hostName}/marketplaces/${shortName}/machine/${macId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            ...(options?.headers ?? {}),
          },
        }
    );
    const data: MachineBaseDto = await response.json();
    if(response.status === 404) {
        throw (response.status);
    }
    return data;
    };

    public getPrivateMachineList = async (
        token: string | null,
        shortName: string | null,
        lang: string,
        queryParams: QueryParams = {}, // Default to an empty object
        options?: RequestInit
        ): Promise<MachineListDto> => {
        // Ensure queryParams is a valid object and include the `language` parameter
        const searchParams = new URLSearchParams({ language: lang });

        Object.entries(queryParams).forEach(([key, value]) => {
            if (Array.isArray(value)) {
            value.forEach((val) => searchParams.append(key, String(val)));
            } else {
            searchParams.append(key, String(value));
            }
        });

        // Construct the final URL with query parameters
        const queryString = searchParams.toString()
            ? `?${searchParams.toString()}`
            : '';
        const url = `${this.private_portal_url}/client/${this.hostName}/marketplaces/${shortName}/search${queryString}`;

        const response = await fetch(url, options);
        const machines: MachineListDto = await response.json();
        return machines;
    };

    public getPrivateMachineListUrl(
        shortName: string | null,
        lang: string,
        queryParams: QueryParams = {}
      ): string {
        const searchParams = new URLSearchParams({ language: lang });
        Object.entries(queryParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((val) => searchParams.append(key, String(val)));
          } else {
            searchParams.append(key, String(value));
          }
        });
    
        const queryString = searchParams.toString()
          ? `?${searchParams.toString()}`
          : '';
        return `${this.private_portal_url}/client/${this.hostName}/marketplaces/${shortName}/search${queryString}`;
      }

    public getPrivateSearchFilters = async (
        shortName: string | null,
        lang: string,
        queryParams: QueryParams = {}, // Default to an empty object
        options?: RequestInit
      ): Promise<SearchFilter[]> => {
        // Ensure queryParams is a valid object and include the `language` parameter
        const searchParams = new URLSearchParams({ language: lang });
    
        Object.entries(queryParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((val) => searchParams.append(key, String(val)));
          } else {
            searchParams.append(key, String(value));
          }
        });
    
        const queryString = searchParams.toString()
          ? `?${searchParams.toString()}`
          : '';
        const url = `${this.private_portal_url}/client/${this.hostName}/marketplaces/${shortName}/search-filters${queryString}`;
    
        const response = await fetch(url, options);
    
        const data: SearchFilter[] = await response.json();
        return data;
      };
      
    public getPrivateSearchFiltersUrl(
        shortName: string | null,
        lang: string,
        queryParams: QueryParams = {}
      ): string {
        const searchParams = new URLSearchParams({ language: lang });
    
        Object.entries(queryParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((val) => searchParams.append(key, String(val)));
          } else {
            searchParams.append(key, String(value));
          }
        });
    
        const queryString = searchParams.toString()
          ? `?${searchParams.toString()}`
          : '';
        return `${this.private_portal_url}/client/${this.hostName}/marketplaces/${shortName}/search-filters${queryString}`;
      }

      public getNewPrivateSearchFiltersUrl(
        shortName: string | null,
        lang: string,
        queryParams: QueryParams = {}
      ): string {
        const searchParams = new URLSearchParams({ language: lang });
    
        Object.entries(queryParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((val) => searchParams.append(key, String(val)));
          } else {
            searchParams.append(key, String(value));
          }
        });
    
        const queryString = searchParams.toString()
          ? `?${searchParams.toString()}`
          : '';
        return `${this.private_portal_url}/client/${this.hostName}/marketplaces/${shortName}/search-filters2${queryString}`;
      }

}